import React, { useState, useEffect } from 'react';
import {
  MessageSquare,
  Plus,
  Edit,
  Trash2,
  Copy,
  Send,
  Search,
  Filter,
  Tag,
  Clock,
  User,
  X
} from 'lucide-react';
import { useAuthStore } from '../../store/authStore';
import { supabase } from '../../lib/supabase';

interface MessageTemplate {
  id: string;
  name: string;
  subject: string;
  content: string;
  category: 'WELCOME' | 'FOLLOW_UP' | 'DOCUMENT_REQUEST' | 'MEETING_INVITE' | 'STATUS_UPDATE' | 'REMINDER' | 'CUSTOM';
  variables: string[];
  usage_count: number;
  created_at: string;
  updated_at: string;
}

interface TemplateVariable {
  key: string;
  label: string;
  example: string;
}

const TEMPLATE_VARIABLES: TemplateVariable[] = [
  { key: '{{PORTEUR_NAME}}', label: 'Nom du porteur', example: 'Marie Dupont' },
  { key: '{{PROJECT_NAME}}', label: 'Nom du projet', example: 'Ferme Agroécologique' },
  { key: '{{ADVISOR_NAME}}', label: 'Nom accompagnateur', example: 'Sophie Martin' },
  { key: '{{NEXT_MEETING}}', label: 'Prochain RDV', example: '15 janvier à 14h' },
  { key: '{{PROJECT_STATUS}}', label: 'Statut projet', example: 'En cours' },
  { key: '{{MISSING_DOCS}}', label: 'Documents manquants', example: 'KBIS, RIB' },
  { key: '{{DEADLINE}}', label: 'Échéance', example: '30 janvier 2024' }
];

const DEFAULT_TEMPLATES: MessageTemplate[] = [
  {
    id: '1',
    name: 'Bienvenue nouveau porteur',
    subject: 'Bienvenue dans l\'accompagnement ESS !',
    content: `Bonjour {{PORTEUR_NAME}},

Je suis {{ADVISOR_NAME}}, votre accompagnateur pour le projet "{{PROJECT_NAME}}".

Je vous félicite pour votre initiative dans l'Économie Sociale et Solidaire ! Je serai à vos côtés tout au long de ce parcours pour vous aider à concrétiser votre projet.

Prochaines étapes :
- Finalisation de votre dossier
- Planification de notre premier rendez-vous
- Définition de la feuille de route

N'hésitez pas à me contacter pour toute question.

Cordialement,
{{ADVISOR_NAME}}
Accompagnateur ESS - CRESS Guadeloupe`,
    category: 'WELCOME',
    variables: ['{{PORTEUR_NAME}}', '{{PROJECT_NAME}}', '{{ADVISOR_NAME}}'],
    usage_count: 15,
    created_at: '2024-01-01T00:00:00Z',
    updated_at: '2024-01-01T00:00:00Z'
  },
  {
    id: '2',
    name: 'Demande de documents',
    subject: 'Documents manquants pour {{PROJECT_NAME}}',
    content: `Bonjour {{PORTEUR_NAME}},

J'ai examiné votre dossier pour le projet "{{PROJECT_NAME}}" et il manque quelques documents pour pouvoir avancer :

Documents manquants :
{{MISSING_DOCS}}

Merci de les téléverser sur la plateforme avant le {{DEADLINE}}.

Si vous avez des questions sur ces documents, n'hésitez pas à me contacter.

Cordialement,
{{ADVISOR_NAME}}`,
    category: 'DOCUMENT_REQUEST',
    variables: ['{{PORTEUR_NAME}}', '{{PROJECT_NAME}}', '{{MISSING_DOCS}}', '{{DEADLINE}}', '{{ADVISOR_NAME}}'],
    usage_count: 8,
    created_at: '2024-01-01T00:00:00Z',
    updated_at: '2024-01-01T00:00:00Z'
  },
  {
    id: '3',
    name: 'Invitation rendez-vous',
    subject: 'Rendez-vous de suivi - {{PROJECT_NAME}}',
    content: `Bonjour {{PORTEUR_NAME}},

Je vous propose un rendez-vous de suivi pour votre projet "{{PROJECT_NAME}}".

Date proposée : {{NEXT_MEETING}}
Lieu : CRESS Guadeloupe, Baie-Mahault

Ordre du jour :
- Point sur l'avancement du projet
- Résolution des éventuels blocages
- Définition des prochaines étapes

Merci de confirmer votre disponibilité.

Cordialement,
{{ADVISOR_NAME}}`,
    category: 'MEETING_INVITE',
    variables: ['{{PORTEUR_NAME}}', '{{PROJECT_NAME}}', '{{NEXT_MEETING}}', '{{ADVISOR_NAME}}'],
    usage_count: 12,
    created_at: '2024-01-01T00:00:00Z',
    updated_at: '2024-01-01T00:00:00Z'
  },
  {
    id: '4',
    name: 'Mise à jour statut',
    subject: 'Évolution de votre projet {{PROJECT_NAME}}',
    content: `Bonjour {{PORTEUR_NAME}},

Bonne nouvelle ! Votre projet "{{PROJECT_NAME}}" vient de passer au statut "{{PROJECT_STATUS}}".

Cela signifie que votre dossier progresse bien dans le processus d'accompagnement ESS.

Prochaines étapes :
- Je vous recontacterai sous 48h pour la suite
- Continuez à compléter les documents si nécessaire

Félicitations pour cette avancée !

Cordialement,
{{ADVISOR_NAME}}`,
    category: 'STATUS_UPDATE',
    variables: ['{{PORTEUR_NAME}}', '{{PROJECT_NAME}}', '{{PROJECT_STATUS}}', '{{ADVISOR_NAME}}'],
    usage_count: 6,
    created_at: '2024-01-01T00:00:00Z',
    updated_at: '2024-01-01T00:00:00Z'
  },
  {
    id: '5',
    name: 'Rappel échéance',
    subject: 'Rappel : Échéance {{DEADLINE}}',
    content: `Bonjour {{PORTEUR_NAME}},

Je me permets de vous rappeler que l'échéance du {{DEADLINE}} approche pour votre projet "{{PROJECT_NAME}}".

Merci de finaliser les éléments en attente pour respecter ce délai.

Si vous rencontrez des difficultés, n'hésitez pas à me contacter rapidement.

Cordialement,
{{ADVISOR_NAME}}`,
    category: 'REMINDER',
    variables: ['{{PORTEUR_NAME}}', '{{PROJECT_NAME}}', '{{DEADLINE}}', '{{ADVISOR_NAME}}'],
    usage_count: 4,
    created_at: '2024-01-01T00:00:00Z',
    updated_at: '2024-01-01T00:00:00Z'
  }
];

export const MessageTemplates: React.FC = () => {
  const { user } = useAuthStore();
  const [templates, setTemplates] = useState<MessageTemplate[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('');
  const [selectedTemplate, setSelectedTemplate] = useState<MessageTemplate | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewData, setPreviewData] = useState({
    porteur_name: 'Marie Dupont',
    project_name: 'Ferme Agroécologique des Antilles',
    advisor_name: user?.full_name || 'Sophie Martin',
    next_meeting: '15 janvier 2024 à 14h',
    project_status: 'En cours',
    missing_docs: 'KBIS, RIB',
    deadline: '30 janvier 2024'
  });

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    try {
      setIsLoading(true);
      const { data, error } = await supabase
        .from('message_templates')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setTemplates(data || []);
    } catch (error) {
      console.error('Error loading templates:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const filteredTemplates = templates.filter(template => {
    const matchesSearch = !searchTerm || 
      template.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      template.content.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesCategory = !categoryFilter || template.category === categoryFilter;
    
    return matchesSearch && matchesCategory;
  });

  const getCategoryLabel = (category: string) => {
    const labels = {
      'WELCOME': 'Bienvenue',
      'FOLLOW_UP': 'Suivi',
      'DOCUMENT_REQUEST': 'Demande documents',
      'MEETING_INVITE': 'Invitation RDV',
      'STATUS_UPDATE': 'Mise à jour statut',
      'REMINDER': 'Rappel',
      'CUSTOM': 'Personnalisé'
    };
    return labels[category as keyof typeof labels] || category;
  };

  const getCategoryColor = (category: string) => {
    const colors = {
      'WELCOME': 'bg-green-100 text-green-800',
      'FOLLOW_UP': 'bg-blue-100 text-blue-800',
      'DOCUMENT_REQUEST': 'bg-orange-100 text-orange-800',
      'MEETING_INVITE': 'bg-purple-100 text-purple-800',
      'STATUS_UPDATE': 'bg-indigo-100 text-indigo-800',
      'REMINDER': 'bg-red-100 text-red-800',
      'CUSTOM': 'bg-gray-100 text-gray-800'
    };
    return colors[category as keyof typeof colors] || 'bg-gray-100 text-gray-800';
  };

  const replaceVariables = (content: string, data: any) => {
    let result = content;
    Object.entries(data).forEach(([key, value]) => {
      const variable = `{{${key.toUpperCase()}}}`;
      result = result.replace(new RegExp(variable, 'g'), value as string);
    });
    return result;
  };

  const handleUseTemplate = async (template: MessageTemplate) => {
    try {
      // Copy to clipboard
      const processedContent = replaceVariables(template.content, previewData);
      navigator.clipboard.writeText(processedContent);

      // Update usage count in database
      const { error } = await supabase
        .from('message_templates')
        .update({ usage_count: template.usage_count + 1 })
        .eq('id', template.id);

      if (error) throw error;

      // Update local state
      setTemplates(prev => prev.map(t =>
        t.id === template.id
          ? { ...t, usage_count: t.usage_count + 1 }
          : t
      ));
    } catch (error) {
      console.error('Error updating usage count:', error);
    }
  };

  const handlePreview = (template: MessageTemplate) => {
    setSelectedTemplate(template);
    setShowPreviewModal(true);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Templates de Messages</h1>
          <p className="text-gray-600 mt-1">
            Messages prédéfinis pour gagner du temps dans vos communications
          </p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Plus className="w-4 h-4" />
          <span>Nouveau template</span>
        </button>
      </div>

      {/* Note explicative */}
      <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded-r-lg">
        <div className="flex">
          <div className="flex-shrink-0">
            <MessageSquare className="h-5 w-5 text-blue-500" />
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-blue-800">Comment utiliser les templates</h3>
            <div className="mt-2 text-sm text-blue-700">
              <p className="mb-2">
                1. <strong>Parcourez</strong> les templates disponibles par catégorie ou utilisez la recherche
              </p>
              <p className="mb-2">
                2. <strong>Prévisualisez</strong> le contenu en cliquant sur l'icône message
              </p>
              <p>
                3. <strong>Copiez</strong> le template dans votre presse-papier en cliquant sur l'icône copier, puis personnalisez les variables avant de l'envoyer
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Rechercher un template..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          
          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="">Toutes les catégories</option>
            <option value="WELCOME">Bienvenue</option>
            <option value="FOLLOW_UP">Suivi</option>
            <option value="DOCUMENT_REQUEST">Demande documents</option>
            <option value="MEETING_INVITE">Invitation RDV</option>
            <option value="STATUS_UPDATE">Mise à jour statut</option>
            <option value="REMINDER">Rappel</option>
            <option value="CUSTOM">Personnalisé</option>
          </select>

          <div className="flex items-center space-x-2 text-sm text-gray-600">
            <Tag className="w-4 h-4" />
            <span>{filteredTemplates.length} template{filteredTemplates.length > 1 ? 's' : ''}</span>
          </div>
        </div>
      </div>

      {/* Templates Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {filteredTemplates.map((template) => (
          <div key={template.id} className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 hover:shadow-lg transition-all duration-200">
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1">
                <div className="flex items-center space-x-2 mb-2">
                  <h3 className="font-semibold text-gray-900">{template.name}</h3>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getCategoryColor(template.category)}`}>
                    {getCategoryLabel(template.category)}
                  </span>
                </div>
                <p className="text-sm font-medium text-gray-700 mb-2">{template.subject}</p>
                <p className="text-sm text-gray-600 line-clamp-3">{template.content}</p>
              </div>
              
              <div className="flex items-center space-x-1 ml-4">
                <button
                  onClick={() => handlePreview(template)}
                  className="p-2 text-gray-400 hover:text-gray-600 rounded-lg transition-colors"
                  title="Aperçu"
                >
                  <MessageSquare className="w-4 h-4" />
                </button>
                <button
                  onClick={() => handleUseTemplate(template)}
                  className="p-2 text-blue-600 hover:text-blue-700 rounded-lg transition-colors"
                  title="Utiliser"
                >
                  <Copy className="w-4 h-4" />
                </button>
                <button
                  className="p-2 text-gray-400 hover:text-gray-600 rounded-lg transition-colors"
                  title="Modifier"
                >
                  <Edit className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Template Info */}
            <div className="flex items-center justify-between text-xs text-gray-500 pt-4 border-t border-gray-100">
              <div className="flex items-center space-x-4">
                <div className="flex items-center space-x-1">
                  <Clock className="w-3 h-3" />
                  <span>Utilisé {template.usage_count} fois</span>
                </div>
                <div className="flex items-center space-x-1">
                  <Tag className="w-3 h-3" />
                  <span>{template.variables.length} variables</span>
                </div>
              </div>
              <span>{new Date(template.updated_at).toLocaleDateString('fr-FR')}</span>
            </div>
          </div>
        ))}
      </div>

      {/* Empty State */}
      {filteredTemplates.length === 0 && (
        <div className="text-center py-12">
          <MessageSquare className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Aucun template trouvé</h3>
          <p className="text-gray-500 mb-6">
            {searchTerm || categoryFilter 
              ? 'Aucun template ne correspond aux filtres sélectionnés.'
              : 'Créez votre premier template pour gagner du temps.'
            }
          </p>
          <button
            onClick={() => setShowCreateModal(true)}
            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Créer un template
          </button>
        </div>
      )}

      {/* Variables Helper */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
        <h3 className="font-semibold text-blue-900 mb-4">Variables disponibles</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {TEMPLATE_VARIABLES.map((variable) => (
            <div key={variable.key} className="bg-white rounded-lg p-3 border border-blue-200">
              <div className="flex items-center justify-between mb-1">
                <code className="text-sm font-mono text-blue-600">{variable.key}</code>
                <button
                  onClick={() => navigator.clipboard.writeText(variable.key)}
                  className="text-blue-600 hover:text-blue-700"
                  title="Copier"
                >
                  <Copy className="w-3 h-3" />
                </button>
              </div>
              <p className="text-xs text-gray-600">{variable.label}</p>
              <p className="text-xs text-gray-500 italic">Ex: {variable.example}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Preview Modal */}
      {showPreviewModal && selectedTemplate && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">Aperçu du template</h2>
              <button
                onClick={() => setShowPreviewModal(false)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Sujet</label>
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="font-medium text-gray-900">
                    {replaceVariables(selectedTemplate.subject, previewData)}
                  </p>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Contenu</label>
                <div className="p-4 bg-gray-50 rounded-lg">
                  <pre className="whitespace-pre-wrap text-sm text-gray-900 font-sans">
                    {replaceVariables(selectedTemplate.content, previewData)}
                  </pre>
                </div>
              </div>

              <div className="flex items-center justify-end space-x-3 pt-4 border-t border-gray-200">
                <button
                  onClick={() => setShowPreviewModal(false)}
                  className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  Fermer
                </button>
                <button
                  onClick={() => {
                    handleUseTemplate(selectedTemplate);
                    setShowPreviewModal(false);
                  }}
                  className="flex items-center space-x-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <Copy className="w-4 h-4" />
                  <span>Utiliser ce template</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};